import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  imagingOrderApi,
  FilterImagingOrderDto,
  CreateImagingOrderDto,
  UpdateImagingOrderDto,
} from '../services/imagingOrderApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

export const imagingOrderKeys = {
  all: ['imaging-orders'] as const,
  lists: () => [...imagingOrderKeys.all, 'list'] as const,
  list: (filters?: FilterImagingOrderDto) => [...imagingOrderKeys.lists(), filters] as const,
  details: () => [...imagingOrderKeys.all, 'detail'] as const,
  detail: (id: string) => [...imagingOrderKeys.details(), id] as const,
  stats: () => [...imagingOrderKeys.all, 'stats'] as const,
  byPatient: (patientId: string) => [...imagingOrderKeys.all, 'patient', patientId] as const,
  groups: (filters?: FilterImagingOrderDto) => [...imagingOrderKeys.all, 'groups', filters] as const,
  group: (groupId: string) => [...imagingOrderKeys.all, 'group', groupId] as const,
};

export const useImagingOrders = (filters?: FilterImagingOrderDto) => {
  return useQuery({
    queryKey: imagingOrderKeys.list(filters),
    queryFn: () => imagingOrderApi.getAll(filters),
    staleTime: 30000,
  });
};

// Session-grouped imaging list (one visit per entry) for the "Group by visit"
// view in the imaging module.
export const useImagingOrderGroups = (
  filters?: FilterImagingOrderDto,
  enabled = true,
) => {
  return useQuery({
    queryKey: imagingOrderKeys.groups(filters),
    queryFn: () => imagingOrderApi.listGroups(filters),
    enabled,
    staleTime: 30000,
  });
};

// All imaging orders from one visit (the session detail page).
export const useImagingOrderGroup = (groupId: string) => {
  return useQuery({
    queryKey: imagingOrderKeys.group(groupId),
    queryFn: () => imagingOrderApi.getGroup(groupId),
    enabled: !!groupId,
    staleTime: 30000,
  });
};

export const useImagingOrder = (id: string) => {
  return useQuery({
    queryKey: imagingOrderKeys.detail(id),
    queryFn: () => imagingOrderApi.getById(id),
    enabled: !!id,
    staleTime: 30000,
  });
};

export const useImagingOrdersByPatient = (patientId: string) => {
  return useQuery({
    queryKey: imagingOrderKeys.byPatient(patientId),
    queryFn: () => imagingOrderApi.getByPatient(patientId),
    enabled: !!patientId,
    staleTime: 30000,
  });
};

export const useImagingOrderStats = (patientId?: string) => {
  return useQuery({
    queryKey: [...imagingOrderKeys.stats(), patientId],
    queryFn: () => imagingOrderApi.getStats(patientId),
    staleTime: 60000,
  });
};

export const useCreateImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateImagingOrderDto) => imagingOrderApi.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.stats() });
      toast.success('Imaging order created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create imaging order');
    },
  });
};

export const useUpdateImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateImagingOrderDto }) =>
      imagingOrderApi.update(id, dto),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      toast.success('Imaging order updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update imaging order');
    },
  });
};

export const useScheduleImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledDate }: { id: string; scheduledDate: string }) =>
      imagingOrderApi.schedule(id, scheduledDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.stats() });
      toast.success('Imaging order scheduled');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to schedule imaging order');
    },
  });
};

export const useCompleteImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { reportNotes?: string; reportAttachments?: unknown[] } }) =>
      imagingOrderApi.complete(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.stats() });
      toast.success('Imaging order completed');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to complete imaging order');
    },
  });
};

export const useReviewImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => imagingOrderApi.review(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      toast.success('Imaging order marked as reviewed');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to review imaging order');
    },
  });
};

export const useCancelImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => imagingOrderApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.stats() });
      toast.success('Imaging order cancelled');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to cancel imaging order');
    },
  });
};

export const useDeleteImagingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => imagingOrderApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: imagingOrderKeys.stats() });
      toast.success('Imaging order deleted');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to delete imaging order');
    },
  });
};
